import { IFollowUpQuestion } from "../../../models/ReturnReasonTypes";
import { FOLLOW_UP_QUESTION_TYPE, MEDIA_OPTION_TYPE } from '../constants';

import { uuid } from "../../../services/helpers";
import { IFollowUpQuestionChanges } from "./follow-up-question-store";
import { IFollowUpQuestionAnswerChanges } from "./follow-up-question-answer-store";


const getSuffix = () => {
    return '_#' + uuid().split('-')[4];
}

export const generateQuestion = (clientId: number|string|undefined, type?: FOLLOW_UP_QUESTION_TYPE, weight: number = 1): IFollowUpQuestionChanges => {    
    const suffix = getSuffix();

    const questionInitialTemplate: IFollowUpQuestionChanges = {
        _client_id: clientId,
        name: '',
        type,
        weight: weight,
        is_enabled: false,
        is_required: false,
        merchant_return_reason_fuq_answers: [],
    };

    let question: IFollowUpQuestionChanges = {} as IFollowUpQuestionChanges;
    switch (type) {
        case FOLLOW_UP_QUESTION_TYPE.FreeText:
            question = {
                ...questionInitialTemplate, 
                ...{
                    question: 'GENERIC_QUESTION' + suffix,
                    instruction: 'PLEASE_WRITE_RETURN_REASON' + suffix,
                    image_instruction: 'WHY_DO_WE_NEED_THIS_PICTURE' + suffix,
                    is_image_requested: false,
                    is_image_required: false,
                    media_option: MEDIA_OPTION_TYPE.ONLY_IMAGE,
                }
            };

            break;
        case FOLLOW_UP_QUESTION_TYPE.ImageRequest:
            question = {
                ...questionInitialTemplate,
                ...{
                    question: 'WHY_DO_WE_NEED_THIS_PICTURE' + suffix,
                    instruction: 'WHY_DO_WE_NEED_THIS_PICTURE_ANSWER' + suffix,
                    media_option: MEDIA_OPTION_TYPE.ONLY_IMAGE,
                }
            };

            break;
        case FOLLOW_UP_QUESTION_TYPE.MultiChoiceMultipleAnswer:
            question = {
                ...questionInitialTemplate,
                ...{
                    question: 'GENERIC_QUESTION' + suffix,
                    instruction: 'PLEASE_CHOOSE_FROM_OPTIONS' + suffix,
                    merchant_return_reason_fuq_answers: [
                        generateAnswer(clientId, type, 0),
                        generateAnswer(clientId, type, 1),
                    ],    
                }
            };

            break;
        case FOLLOW_UP_QUESTION_TYPE.MultiChoiceSingleAnswer:
            question = {
                ...questionInitialTemplate,
                ...{
                    question: 'GENERIC_QUESTION' + suffix,
                    instruction: 'PLEASE_CHOOSE_ONE_OPTION' + suffix,
                    merchant_return_reason_fuq_answers: [
                        generateAnswer(clientId, type, 0),
                        generateAnswer(clientId, type, 1),
                    ],    
                }
            };

            break;
        case FOLLOW_UP_QUESTION_TYPE.NumericStepper:
            question = {
                ...questionInitialTemplate,
                ...{
                    question: 'GENERIC_QUESTION' + suffix,
                    instruction: 'USE_PLUS_MINUS_BUTTONS_TO_SET_NUMBER_OF_DAYS' + suffix,
                    stepper_hint: 'E_G_DAYS' + suffix,
                    stepper_default_value: 5,
                    stepper_min_value: 1,
                    stepper_max_value: 99,
                }
            };

            break;
        case FOLLOW_UP_QUESTION_TYPE.YesNo:
            question = {
                ...questionInitialTemplate,
                ...{
                    question: 'GENERIC_QUESTION' + suffix,
                    instruction: 'PLEASE_CHOOSE_ONE_OPTION' + suffix,
                    merchant_return_reason_fuq_answers: [
                        generateAnswer(clientId, type, 0),
                        generateAnswer(clientId, type, 1),
                    ],   
                }
            };
            break;

        default: 
            question = questionInitialTemplate;
            break;
    }

    return question as IFollowUpQuestion;
} 

export const generateAnswer = (
    fuq_id: number|string|undefined, 
    type: FOLLOW_UP_QUESTION_TYPE, 
    index: number = 0,
    weight?: number ): IFollowUpQuestionAnswerChanges => 
{
    const clientId = uuid();
    const suffix = getSuffix();
    const newWeight = weight ?? index;

    const answerInitialTemplate = {
        _client_id: clientId,
        return_reasons_fuq_id: fuq_id,
        weight: newWeight,
    };

    var answer = {};
    switch (type) {
        case FOLLOW_UP_QUESTION_TYPE.MultiChoiceSingleAnswer:
        case FOLLOW_UP_QUESTION_TYPE.MultiChoiceMultipleAnswer:
            answer = {
                display_name: 'GENERIC_ANSWER' + suffix,
            };

            break;
        case FOLLOW_UP_QUESTION_TYPE.YesNo:
            const yesNoKeys = [
                'GENERIC_ANSWER_YES',
                'GENERIC_ANSWER_NO',
            ];

            answer = {
                display_name: yesNoKeys[index] + suffix,
            };

            break;
    }

    return {
        ...answerInitialTemplate,
        ...answer,
    };
}

/** map of mandatory fields per type */
const QUESTION_TYPE_FIEDS_MAP = {
    [FOLLOW_UP_QUESTION_TYPE.FreeText]: [
        'question',
        'instruction',
        'image_instruction',
        'is_image_requested',
        'is_image_required',
        'media_option',
    ],
    [FOLLOW_UP_QUESTION_TYPE.ImageRequest]: [
        'question',
        'instruction',
        'media_option',
    ],
    [FOLLOW_UP_QUESTION_TYPE.MultiChoiceMultipleAnswer]: [
        'question',
        'instruction',
    ],
    [FOLLOW_UP_QUESTION_TYPE.MultiChoiceSingleAnswer]: [
        'question',
        'instruction',
    ],
    [FOLLOW_UP_QUESTION_TYPE.NumericStepper]: [
        'question',
        'instruction',
        'stepper_hint',
        'stepper_default_value',
        'stepper_min_value',
        'stepper_max_value',
    ],
    [FOLLOW_UP_QUESTION_TYPE.YesNo]: [
        'question',
        'instruction',
    ],
}

/** fields that must present in all types */
const QUESTION_COMMON_FIELDS = [
    '_client_id',
    '_deleted',
    
    'id',
    'name',
    'type',
    'weight',
    'is_enabled',
    'is_required',
    'merchant_return_reason_fuq_answers',
]

export function cleanupQuestionModel(model: IFollowUpQuestion): IFollowUpQuestion {
    const fields = [ ...QUESTION_COMMON_FIELDS, ...QUESTION_TYPE_FIEDS_MAP[model.type] ];
    let newModel = {} as IFollowUpQuestion;
    fields.forEach(field => {
        newModel = {...newModel, ...{[field]: model[field as keyof IFollowUpQuestion]} };
    });
    return newModel;
}
